import * as React from 'react';
import { useState } from 'react';
import type { AnyFieldApi } from '@tanstack/react-form';
import { defineFormPage, useFormPage } from '@repo/form';
import { FormActions, fieldError as fe, DynamicContactList, type ContactItem } from '@repo/web-form';
import { z } from 'zod';
import { useNavigate } from '@tanstack/react-router';
import { useCreateStaff, useRoles } from '../network/staff.api';
import { useOrgUnits } from '../../org/network/org.api';
import { OrgUnitPicker } from '../../org/components/OrgUnitPicker';
import { FormField, Input } from '../../../components/ui/FormField';
import { FormSelect } from '../../../components/ui/FormSelect';
import { useToast } from '../../../components/ui/Toast';
import { Banner } from '../../../components/ui/Banner.js';
import { TempPasswordDialog } from './TempPasswordDialog';

const createStaffSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Please enter a valid email'),
  phoneNumber: z
    .string()
    .regex(/^[6-9]\d{9}$/, 'Must be a valid 10-digit Indian mobile number')
    .optional()
    .or(z.literal('')),
  orgUnitId: z.string().uuid('Please select a branch'),
  roleId: z.string().uuid('Please select a role'),
  companyWide: z.boolean().optional(),
});

type StaffFormValues = z.infer<typeof createStaffSchema>;

const staffFormConfig = defineFormPage<StaffFormValues>({
  resource: 'staff',
  defaultValues: {
    name: '',
    email: '',
    phoneNumber: '',
    orgUnitId: '',
    roleId: '',
    companyWide: false,
  },
  schema: createStaffSchema,
});

export function CreateStaffForm() {
  const navigate = useNavigate();
  const createMutation = useCreateStaff();
  const { data: roles } = useRoles();
  const { data: orgUnits } = useOrgUnits();
  const { toast } = useToast();
  const [createdTempPassword, setCreatedTempPassword] = useState<string | null>(null);
  const [createdStaffName, setCreatedStaffName] = useState<string>('');

  const [contacts, setContacts] = useState<ContactItem[]>([
    { type: 'EMAIL', label: 'WORK', value: '', isPrimary: true },
    { type: 'PHONE', label: 'MOBILE', value: '', isPrimary: true },
  ]);

  const {
    form,
    isSubmitting,
    submissionAttempts,
    handleSubmit,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(staffFormConfig, createMutation, {
    routeId: 'admin/staff/new',
    onBeforeSubmit: (value) => {
      const primaryEmail = contacts.find((c) => c.type === 'EMAIL' && c.isPrimary)?.value || contacts.find((c) => c.type === 'EMAIL')?.value || value.email;
      const primaryPhone = contacts.find((c) => c.type === 'PHONE' && c.isPrimary)?.value || contacts.find((c) => c.type === 'PHONE')?.value || value.phoneNumber;
      return {
        ...value,
        email: primaryEmail,
        phoneNumber: primaryPhone || undefined,
        contacts,
      };
    },
    onSuccess: (data?: unknown) => {
      const res = data as { tempPassword?: string } | undefined;
      if (res?.tempPassword) {
        setCreatedTempPassword(res.tempPassword);
        setCreatedStaffName(form.getFieldValue('name') || 'Staff member');
      } else {
        toast({
          title: 'Staff Created',
          description: 'Staff account has been successfully created.',
          type: 'success',
        });
        void navigate({ to: '/admin/staff' });
      }
    },
    onError: (err: { message?: string }) => {
      toast({
        title: 'Error',
        description: err.message || 'Failed to create staff member',
        type: 'error',
      });
    },
  });

  const roleOptions = React.useMemo(() => {
    if (!roles) return [];
    return roles.map((r) => ({ value: r.id, label: r.name }));
  }, [roles]);

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      {hasDraft && (
        <Banner
          variant="info"
          title="Restore Draft"
          description="You have an unsaved draft from a previous session. Would you like to restore it?"
          action={{
            label: 'Restore',
            onClick: () => {
              const draft = restoreDraft();
              if (draft) {
                Object.entries(draft).forEach(([key, val]) => {
                  form.setFieldValue(key as never, val as never);
                });
              }
              dismissDraft();
            },
          }}
          secondaryAction={{
            label: 'Discard',
            onClick: () => {
              clearDraft();
            },
          }}
        />
      )}

      <form.Field
        name="name"
        validators={{ onChange: ({ value }: { value: unknown }) => createStaffSchema.shape.name.safeParse(value).success ? undefined : createStaffSchema.shape.name.safeParse(value).error?.issues[0]?.message }}
        children={(field: AnyFieldApi) => (
          <FormField
            label="Full Name"
            required
            error={fe(field.state.meta, submissionAttempts)}
          >
            <Input
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="e.g. John Doe"
              disabled={isSubmitting}
            />
          </FormField>
        )}
      />

      <div className="border-t pt-4">
        <DynamicContactList
          contacts={contacts}
          disabled={isSubmitting}
          onChange={(newContacts) => {
            setContacts(newContacts);
            const primaryEmail = newContacts.find((c) => c.type === 'EMAIL' && c.isPrimary)?.value || '';
            const primaryPhone = newContacts.find((c) => c.type === 'PHONE' && c.isPrimary)?.value || '';
            if (primaryEmail) form.setFieldValue('email', primaryEmail);
            if (primaryPhone) form.setFieldValue('phoneNumber', primaryPhone);
          }}
        />
      </div>

      <form.Field
        name="orgUnitId"
        validators={{ onChange: ({ value }: { value: unknown }) => createStaffSchema.shape.orgUnitId.safeParse(value).success ? undefined : createStaffSchema.shape.orgUnitId.safeParse(value).error?.issues[0]?.message }}
        children={(field: AnyFieldApi) => (
          <FormField
            label="Branch"
            required
            error={fe(field.state.meta, submissionAttempts)}
          >
            <OrgUnitPicker
              nodes={orgUnits || []}
              value={field.state.value}
              onChange={field.handleChange}
              unitType="BRANCH"
              placeholder="Select branch..."
              disabled={isSubmitting}
            />
          </FormField>
        )}
      />

      <form.Field
        name="roleId"
        validators={{ onChange: ({ value }: { value: unknown }) => createStaffSchema.shape.roleId.safeParse(value).success ? undefined : createStaffSchema.shape.roleId.safeParse(value).error?.issues[0]?.message }}
        children={(field: AnyFieldApi) => (
          <FormSelect
            label="Role"
            required
            options={[{ value: '', label: 'Select role...' }, ...roleOptions]}
            value={field.state.value}
            onChange={(e) => field.handleChange(e.target.value)}
            error={fe(field.state.meta, submissionAttempts)}
            disabled={isSubmitting}
          />
        )}
      />

      <form.Field
        name="companyWide"
        children={(field: AnyFieldApi) => (
          <label className="flex items-start gap-2 cursor-pointer pt-1">
            <Input
              type="checkbox"
              checked={Boolean(field.state.value)}
              onChange={(e) => field.handleChange(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border text-accent-500 focus:ring-accent-500 cursor-pointer"
              disabled={isSubmitting}
            />
            <span className="text-xs">
              <span className="font-semibold text-text-primary block">Company-wide access</span>
              <span className="text-text-secondary">
                Lifts branch scoping so this role applies across all branches.
              </span>
            </span>
          </label>
        )}
      />

      <FormActions onCancel={() => void navigate({ to: '/admin/staff' })} isSubmitting={isSubmitting} submitLabel="Create Staff" />

      <TempPasswordDialog
        open={Boolean(createdTempPassword)}
        tempPassword={createdTempPassword || ''}
        userName={createdStaffName}
        title="Staff Account Created"
        description="A temporary password has been generated for this account. Please copy and share it securely with the staff member. This password will not be shown again."
        onClose={() => {
          setCreatedTempPassword(null);
          void navigate({ to: '/admin/staff' });
        }}
      />
    </form>
  );
}
